import { WORLD_MAP_HEIGHT, WORLD_MAP_WIDTH } from './WorldMapSource';

export const WORLD_MAP_MIN_ZOOM = 1;
export const WORLD_MAP_MAX_ZOOM = 12;

const zoomStep = 1.5;
const panStepFraction = 0.18;
const comparisonTolerance = 0.0001;

export type MapPanDirection = 'north' | 'east' | 'south' | 'west';

export interface WorldMapCamera {
  center: [number, number];
  zoom: number;
}

export class WorldMapViewportController {
  private camera: WorldMapCamera = createDefaultCamera();

  public getCamera(): WorldMapCamera {
    return copyCamera(this.camera);
  }

  public accept(center: readonly number[] | null, zoom: number | null): WorldMapCamera {
    const requestedCenter = readCenter(center) ?? this.camera.center;
    const requestedZoom = typeof zoom === 'number' && Number.isFinite(zoom) ? zoom : this.camera.zoom;
    this.camera = constrainCamera({ center: requestedCenter, zoom: requestedZoom });
    return this.getCamera();
  }

  public zoomIn(): WorldMapCamera {
    return this.setZoom(this.camera.zoom * zoomStep);
  }

  public zoomOut(): WorldMapCamera {
    return this.setZoom(this.camera.zoom / zoomStep);
  }

  public pan(direction: MapPanDirection): WorldMapCamera {
    const visibleWidth = WORLD_MAP_WIDTH / this.camera.zoom;
    const visibleHeight = WORLD_MAP_HEIGHT / this.camera.zoom;
    let centerX = this.camera.center[0];
    let centerY = this.camera.center[1];

    if (direction === 'west') centerX -= visibleWidth * panStepFraction;
    if (direction === 'east') centerX += visibleWidth * panStepFraction;
    if (direction === 'north') centerY -= visibleHeight * panStepFraction;
    if (direction === 'south') centerY += visibleHeight * panStepFraction;

    this.camera = constrainCamera({ center: [centerX, centerY], zoom: this.camera.zoom });
    return this.getCamera();
  }

  public reset(): WorldMapCamera {
    this.camera = createDefaultCamera();
    return this.getCamera();
  }

  public canZoomIn(): boolean {
    return this.camera.zoom < WORLD_MAP_MAX_ZOOM - comparisonTolerance;
  }

  public canZoomOut(): boolean {
    return this.camera.zoom > WORLD_MAP_MIN_ZOOM + comparisonTolerance;
  }

  public canPan(direction: MapPanDirection): boolean {
    const bounds = createCenterBounds(this.camera.zoom);
    if (direction === 'west') return this.camera.center[0] > bounds.minimumX + comparisonTolerance;
    if (direction === 'east') return this.camera.center[0] < bounds.maximumX - comparisonTolerance;
    if (direction === 'north') return this.camera.center[1] > bounds.minimumY + comparisonTolerance;
    return this.camera.center[1] < bounds.maximumY - comparisonTolerance;
  }

  private setZoom(zoom: number): WorldMapCamera {
    this.camera = constrainCamera({ center: this.camera.center, zoom });
    return this.getCamera();
  }
}

function constrainCamera(camera: WorldMapCamera): WorldMapCamera {
  const zoom = Math.min(WORLD_MAP_MAX_ZOOM, Math.max(WORLD_MAP_MIN_ZOOM, camera.zoom));
  const bounds = createCenterBounds(zoom);
  return {
    center: [
      Math.min(bounds.maximumX, Math.max(bounds.minimumX, camera.center[0])),
      Math.min(bounds.maximumY, Math.max(bounds.minimumY, camera.center[1])),
    ],
    zoom,
  };
}

function createCenterBounds(zoom: number): {
  minimumX: number;
  maximumX: number;
  minimumY: number;
  maximumY: number;
} {
  const halfVisibleWidth = WORLD_MAP_WIDTH / (zoom * 2);
  const halfVisibleHeight = WORLD_MAP_HEIGHT / (zoom * 2);
  return {
    minimumX: halfVisibleWidth,
    maximumX: WORLD_MAP_WIDTH - halfVisibleWidth,
    minimumY: halfVisibleHeight,
    maximumY: WORLD_MAP_HEIGHT - halfVisibleHeight,
  };
}

function createDefaultCamera(): WorldMapCamera {
  return { center: [WORLD_MAP_WIDTH / 2, WORLD_MAP_HEIGHT / 2], zoom: WORLD_MAP_MIN_ZOOM };
}

function readCenter(value: readonly number[] | null): [number, number] | null {
  if (value === null || value.length !== 2) return null;
  const centerX = value[0];
  const centerY = value[1];
  if (typeof centerX !== 'number' || typeof centerY !== 'number' || !Number.isFinite(centerX) || !Number.isFinite(centerY)) return null;
  return [centerX, centerY];
}

function copyCamera(camera: WorldMapCamera): WorldMapCamera {
  return { center: [camera.center[0], camera.center[1]], zoom: camera.zoom };
}
